import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { Button, Icons, InformationModal, SkeletonText } from 'utoniq-ui'

import { Item, PickupUserFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { OrderItem, SwipeButton } from '@/components/organisms'
import { config } from '@/config'
import { useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

import { ReceiptTag } from '../../MyOwnedTickets/template'
export * from './fragments'

export type State = {
  loading: boolean
  submitting: boolean
  pickupUser: PickupUserFragment | null
  items: PickupUserFragment['userItems']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onUseTicket: () => Promise<void>
}

export const MyOwnedTicketDetailTemplate: Template<State, Action> = ({
  state,
  onUseTicket,
}) => {
  const { t } = useTranslation('myOwnedTicketDetail')
  return (
    <BaseLayout margin header={{ back: true, title: t('title') }}>
      {state?.pickupUser?.isExpired && <ExpiredCell />}
      {state.loading ? (
        <TicketDetailSkeleton />
      ) : (
        <TicketDetailCell
          pickupUser={state.pickupUser}
          onUseTicket={onUseTicket}
          loading={state.loading}
          submitting={state.submitting}
          items={state.items}
        />
      )}
    </BaseLayout>
  )
}

type TicketDetailCellProps = {
  pickupUser: State['pickupUser']
  loading: boolean
  submitting: boolean
  onUseTicket: Action['onUseTicket']
  items: State['items']
}
const TicketDetailCell: Cell<TicketDetailCellProps> = ({
  pickupUser,
  onUseTicket,
  loading,
  submitting,
  items,
}) => {
  const { t } = useTranslation('myOwnedTicketDetail')
  const { analytics } = useAnalytics()
  const { onClick: onClickMapLink } = useBrowserLink(
    pickupUser?.event?.location?.mapUri ?? ''
  )

  if (!pickupUser) return null

  return (
    <CellFrame sx={{ pb: 5 }}>
      <Card elevation={0}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Box sx={{ pt: 3, pb: 5 }}>
            {!!pickupUser.user && (
              <>
                <Typography variant="h5" color="text.secondary">
                  {t('customerInfo', 'お客様情報')}
                </Typography>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ pt: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('accountName', 'アカウント名')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {pickupUser.user.displayName}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ pt: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('email', 'メールアドレス')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {pickupUser.user.email}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ pt: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('fullName', '氏名')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {pickupUser.user.fullName}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ pt: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('phoneNumber', '電話番号')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {pickupUser.user.formattedPhoneNumber}
                  </Typography>
                </Stack>
              </>
            )}
          </Box>

          <Typography variant="h5" sx={{ py: 3 }} color="text.secondary">
            {t('ticketInfo', '受け取り先情報')}
          </Typography>

          {/* コレクション画像のエリア */}
          <CollectionInfoCell pickupUser={pickupUser} />

          {/* 受け取り時間 */}
          <Stack direction="row" spacing={1} sx={{ mt: 5 }}>
            <Typography variant="subtitle1">{t('pickupTime')}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {pickupUser.formattedPickupStartEndTime}
            </Typography>
          </Stack>
          {/* 受け取り場所 */}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Typography variant="subtitle1">{t('pickupLocation')}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {pickupUser.eventCollection.pickupLocation}
            </Typography>

            {!!pickupUser?.event?.location?.mapUri && (
              <Stack
                direction="row"
                onClick={() => {
                  analytics?.track('click_eventMapLink', {
                    applicationKey: config.applicationKey,
                    eventId: pickupUser.event?.id,
                    type: 'eventPickup',
                  })
                  onClickMapLink()
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                >
                  {t('venueMap', '会場MAP')}
                </Typography>
              </Stack>
            )}
          </Stack>
          {/* 受け取り商品一覧 */}
          <Stack sx={{ mt: 5 }}>
            <Typography variant="h5" sx={{ py: 3 }} color="text.secondary">
              {t('pickupItems', '受け取り商品')}
            </Typography>
            <Stack spacing={2} sx={{ pb: 6 }}>
              {items?.map(({ item }, index) => {
                const order = pickupUser.pickupOrder.find(
                  ({ itemId }) => itemId === item.id
                )
                // アイテムの個数。SKUがある場合はitemのskuIdに一致するもののみ表示
                const quantity = item.sku
                  ? order?.serials.filter(({ skuId }) => skuId === item.sku?.id)
                      .length
                  : order?.count

                if (!quantity) return null

                return (
                  <OrderItem
                    item={item as Item}
                    key={index}
                    quantity={quantity}
                    sku={item.sku}
                  />
                )
              })}
            </Stack>
          </Stack>

          {/* チケット使用ゾーン */}
          <TicketUseCell
            loading={loading}
            submitting={submitting}
            onUseTicket={onUseTicket}
            pickupUser={pickupUser}
          />
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type CollectionInfoCellProps = {
  pickupUser: PickupUserFragment
}

const CollectionInfoCell: Cell<CollectionInfoCellProps> = ({ pickupUser }) => {
  const { t } = useTranslation('myOwnedTicketDetail')

  const remainingTime = useMemo(() => {
    if (!pickupUser.pickupRemainingTime) return null

    const { hours, minutes, expiring } = pickupUser.pickupRemainingTime
    const remainingTime =
      hours || minutes
        ? [
            hours && hours > 0 ? `${hours}${t('time')}` : '',
            minutes > 0 ? `${minutes}${t('minute')}` : '',
          ].join('')
        : 1

    return expiring
      ? t('countDownTime', { remainingTime })
      : t('countDownTimeUntilPickupStart', { remainingTime })
  }, [pickupUser.pickupRemainingTime, t])

  if (!pickupUser) return null

  return (
    <Stack direction="row" spacing={3} alignItems="flex-start">
      <Avatar
        variant="square"
        src={pickupUser.eventCollection.collection.thumbnail.uri ?? ''}
        sx={{ width: 72, height: 72, borderRadius: '4px' }}
      />
      <Stack flex={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ pb: 2 }}
        >
          <ReceiptTag
            receipted={!!pickupUser.isCompleted}
            expired={!!pickupUser.isExpired}
          />
          {remainingTime && (
            <Stack direction="row" alignItems="center">
              <Icons.Notice
                fontSize="xSmall"
                sx={{
                  color: pickupUser.pickupRemainingTime?.expiring
                    ? 'like.tap'
                    : 'primary.main',
                }}
              />
              <Typography
                variant="overline"
                sx={{
                  color: pickupUser.pickupRemainingTime?.expiring
                    ? 'like.tap'
                    : 'primary.main',
                }}
              >
                {remainingTime}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Typography variant="subtitle1">
          {pickupUser.eventCollection.collection.title}
        </Typography>
        <Stack
          flexDirection="row"
          alignItems="center"
          sx={{
            mt: 1,
            gap: 1,
          }}
        >
          <Avatar
            sx={{ width: '24px', height: '24px' }}
            src={
              pickupUser.eventCollection.collection.artist.avatar?.smallUri ??
              ''
            }
          />
          <Typography variant="overline" color="text.secondary">
            {pickupUser.eventCollection.collection.artist.displayName}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}

const ExpiredCell: Cell = () => {
  const { t } = useTranslation('myOwnedTicketDetail')
  return (
    <CellFrame sx={{ pt: 6, pb: 5 }}>
      <Stack direction="row" justifyContent="center" spacing={1}>
        <Icons.Error color="error" />
        <Typography variant="h5">{t('receiptExpirationTitle')}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ pt: 2 }}>
        <Trans i18nKey="receiptExpirationDesc" t={t} />
      </Typography>
    </CellFrame>
  )
}

type TicketUseCellProps = {
  loading: boolean
  submitting: boolean
  onUseTicket: Action['onUseTicket']
  pickupUser: State['pickupUser']
}
const TicketUseCell: Cell<TicketUseCellProps> = ({
  onUseTicket,
  submitting,
  loading,
  pickupUser,
}) => {
  const { t } = useTranslation('myOwnedTicketDetail')
  const [open, setOpen] = useState<boolean>(false)

  return (
    <>
      <Stack>
        {!loading && pickupUser?.isCompleted ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            <Icons.Success fontSize="xSmall" />
            <Typography variant="overline" color="text.secondary">
              {t('dateOfUse')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
              {pickupUser?.formattedCompletedAt}
            </Typography>
          </Stack>
        ) : (
          <>
            <Typography variant="subtitle1">
              {t(
                'chargeNote',
                'ここから先は必ず担当者に操作してもらってください。'
              )}
            </Typography>
            <Typography variant="caption">
              {t(
                'chargeDesc',
                '一度使用済みになったチケットは、未使用の状態には戻せませんのでご注意ください。'
              )}
            </Typography>
          </>
        )}
      </Stack>
      {pickupUser?.isExpired && (
        <Stack
          sx={{
            mt: 4,
            px: 4,
            py: 2,
            borderColor: 'error.main',
            borderWidth: 1,
            borderStyle: 'solid',
          }}
        >
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
          >
            <Icons.Error color="error" fontSize="small" />
            <Typography variant="subtitle2">
              {t('receiptExpirationTitle')}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ pt: 2 }}>
            <Trans i18nKey="receiptExpirationDesc" t={t} />
          </Typography>
        </Stack>
      )}
      <Stack sx={{ py: 4, px: 6 }} alignItems="center">
        <SwipeButton
          loading={loading}
          color={pickupUser?.isExpired ? 'error' : 'primary'}
          textOn={t('charge', '担当者操作ボタン')}
          textOff={t('used', '使用済み')}
          onClick={async () => {
            setOpen(true)
          }}
          succeed={!!pickupUser?.isCompleted}
        />
        <InformationModal
          open={open}
          onClose={() => {
            setOpen(submitting ?? false)
          }}
          title={t('confirmChargeTitle')}
          content={t('confirmChargeDesc')}
          cancelText={submitting ? '' : t('close')}
          buttons={
            <Button
              loading={submitting}
              size="large"
              fullWidth
              onClick={() => {
                onUseTicket().finally(() => {
                  setOpen(false)
                })
              }}
            >
              {t('ok')}
            </Button>
          }
        />
      </Stack>
    </>
  )
}

const TicketDetailSkeleton = () => (
  <CellFrame>
    <Card elevation={0}>
      <CardActionArea>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 4 } }}>
          <Box sx={{ mb: 5, mt: 3 }}>
            <Skeleton variant="rectangular" width={160} height={16} />
          </Box>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Skeleton variant="rectangular" width={90} height={64} />
            <Stack flex={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ pb: 2 }}
              >
                <Stack flex={1}>
                  <SkeletonText variant="medium" />
                </Stack>
                <Stack flex={3}></Stack>
              </Stack>
              <SkeletonText variant="subtitle1" />
              <Stack sx={{ pt: 1 }}>
                <SkeletonText variant="caption" width="60%" />
              </Stack>
            </Stack>
          </Stack>
          <Stack flex={1} spacing={1} sx={{ px: 4, py: 2 }}>
            <SkeletonText variant="body1" width="100%" />
            <SkeletonText variant="body1" width="100%" />
          </Stack>
          <Box sx={{ mt: 5, mb: 5 }}>
            <Skeleton variant="rectangular" width={160} height={16} />
          </Box>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Skeleton variant="rectangular" width={60} height={96} />
            <Stack flex={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ pb: 2 }}
              >
                <Stack flex={1}>
                  <SkeletonText variant="medium" />
                </Stack>
                <Stack flex={3}></Stack>
              </Stack>
              <SkeletonText variant="subtitle1" />
              <Stack sx={{ pt: 1 }}>
                <SkeletonText variant="caption" width="60%" />
              </Stack>
            </Stack>
          </Stack>
          <Stack flex={1} spacing={1} sx={{ px: 4, py: 2 }}>
            <SkeletonText variant="body1" width="100%" />
            <SkeletonText variant="body1" width="100%" />
          </Stack>
          <Box sx={{ mt: 5, display: 'grid', placeItems: 'center', pb: 8 }}>
            <SwipeButton
              disabled
              loading={false}
              succeed={false}
              textOn=""
              textOff=""
              onClick={async () => {}}
            />
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  </CellFrame>
)
